/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { Grid, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

/**
 * The internal dependencies
 */
import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  CreateChangeDto,
  GetChangeDto,
  UpdateChangeDto,
} from '@codefluegel/zeta-change-typescript-client';
import { useTranslation } from 'react-i18next';
import {
  checkIfRejected,
  disableInput,
  showImplementation,
} from '../../utils/permissionHelper';
import LocalizationWrapper from '../../components/Date/LocalizationWrapper';

export type ProjectEditCustomerPageProps = {
  isTabDataVisible: boolean;
  control: Control<CreateChangeDto & UpdateChangeDto, any>;
  change: GetChangeDto | null;
};

const ProjectEditCustomerPage = observer(
  ({ isTabDataVisible, control, change }: ProjectEditCustomerPageProps) => {
    const [isDesktopDatePickerOpen, setIsDesktopDatePickerOpen] =
      useState(false);

    const { t } = useTranslation();
    const permissionToEdit = disableInput(change);

    const editFields = checkIfRejected(change) || showImplementation(change);

    const onDesktopDatePickerFocus = (e: any) => {
      setIsDesktopDatePickerOpen(true);
      e.target.blur();
    };

    return (
      <Grid
        container
        direction="column"
        css={css`
          display: ${isTabDataVisible ? 'flex' : 'none'};
          padding: 19px 0px 12px 0px;
        `}
      >
        <Controller
          control={control}
          name="changeNoClient"
          defaultValue=""
          render={({ field }) => (
            <TextField
              css={css`
                .MuiInput-underline:before {
                  border-bottom: 1px solid #ececec;
                }
              `}
              label={t('change.changeNoClient')}
              value={field.value}
              onChange={field.onChange}
              disabled={editFields || permissionToEdit}
              variant="standard"
              InputProps={{
                style: {
                  paddingLeft: '16px',
                  paddingBottom: '6px',
                  marginBottom: '16px',
                  fontSize: '14px',
                },
              }}
              InputLabelProps={{
                style: {
                  paddingLeft: '20px',
                  fontWeight: 600,
                  color: '#333333',
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="projectManagerClient"
          defaultValue=""
          render={({ field }) => (
            <TextField
              css={css`
                .MuiInput-underline:before {
                  border-bottom: 1px solid #ececec;
                }
              `}
              label={t('change.projectManagerClient')}
              value={field.value}
              onChange={field.onChange}
              disabled={editFields || permissionToEdit}
              variant="standard"
              InputProps={{
                style: {
                  paddingLeft: '16px',
                  paddingBottom: '6px',
                  marginBottom: '16px',
                  fontSize: '14px',
                },
              }}
              InputLabelProps={{
                style: {
                  paddingLeft: '20px',
                  fontWeight: 600,
                  color: '#333333',
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="submisionCRByClient"
          defaultValue={new Date().toISOString()}
          render={({ field }) => (
            <LocalizationWrapper>
              <DesktopDatePicker
                open={isDesktopDatePickerOpen}
                onOpen={() => setIsDesktopDatePickerOpen(true)}
                onClose={() => setIsDesktopDatePickerOpen(false)}
                format="dd.MM.yyyy"
                label={t('change.submisionCRByClient')}
                disabled={editFields || permissionToEdit}
                value={new Date(field.value || '')}
                onChange={field.onChange}
                slotProps={{
                  textField: {
                    style: { width: '100%', marginTop: 16 },
                    variant: 'standard',
                    InputProps: {
                      onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                        onDesktopDatePickerFocus(e),
                      sx: {
                        marginTop: 3,
                        paddingLeft: 2,
                        '& .MuiSvgIcon-root': {
                          color: '#80acd4',
                        },
                      },
                    },
                    InputLabelProps: {
                      sx: {
                        paddingLeft: '20px',
                        fontWeight: 600,
                        color: '#333333',
                      },
                    },
                  },
                  inputAdornment: { sx: { paddingRight: 1 } },
                }}
              />
            </LocalizationWrapper>
          )}
        />
      </Grid>
    );
  },
);

export default ProjectEditCustomerPage;
